/**
 * Old-URL handling that only the browser can do (`docs/design/ia.md` §2): the legacy UI used
 * hash routes (`/#/m/{key}`) and a `?run=` query, neither of which reaches the server. Runs
 * once in main.tsx, before the router mounts.
 */

export interface LocationLike {
  pathname: string;
  search: string;
  hash: string;
}

function query(hashQuery: string): URLSearchParams {
  return new URLSearchParams(hashQuery.startsWith("?") ? hashQuery.slice(1) : hashQuery);
}

/** The new path (with query) for a legacy location, or null when nothing needs redirecting. */
export function legacyRedirectTarget(location: LocationLike): string | null {
  const hash = location.hash;
  if (hash.startsWith("#/")) {
    const body = hash.slice(2); // after "#/"
    const [route = "", rawQuery = ""] = body.split("?", 2);
    const params = query(rawQuery);
    if (route === "") {
      const q = params.get("q");
      return q ? `/?${new URLSearchParams({ q }).toString()}` : "/";
    }
    if (route === "add") return "/upload";
    if (route.startsWith("m/")) {
      const key = safeDecode(route.slice(2));
      if (key === "") return "/";
      const assembly = params.get("a");
      const search = assembly ? `?${new URLSearchParams({ assembly }).toString()}` : "";
      return `/machines/${encodeURIComponent(key)}${search}`;
    }
    return null;
  }
  if (location.pathname === "/" && location.search) {
    const run = new URLSearchParams(location.search).get("run");
    if (run) return `/runs/${encodeURIComponent(run)}`;
  }
  return null;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Replace the current history entry when the URL is a legacy one. Returns the new path or null. */
export function applyLegacyRedirects(
  location: LocationLike = window.location,
  history: Pick<History, "replaceState"> = window.history,
): string | null {
  const target = legacyRedirectTarget(location);
  if (target) history.replaceState(null, "", target);
  return target;
}
